import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  Select,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { RepeatIcon, SearchIcon, SmallCloseIcon } from '@chakra-ui/icons';

import BaraFeatureGuard from 'components/bara/BaraFeatureGuard';
import Card from 'components/card/Card';
import {
  apiListBranchHolidays,
  apiAddBranchHoliday,
  apiDeleteBranchHoliday,
  apiListBranchAuthority,
  apiGrantBranchAuthority,
  apiRevokeBranchAuthority,
  apiGetBranchMembers,
} from 'api';
import type {
  APIBaraHoliday,
  APIBaraAuthority,
  APIBaraBranchMember,
} from 'types/api.types';

const AUTHORITY_TYPE_COLORS: Record<number, string> = {
  1: 'purple',
  2: 'blue',
};

const ROLE_OPTIONS = [
  { value: 2, label: 'Underwriter' },
  { value: 3, label: 'Broker' },
  { value: 4, label: 'Manager' },
  { value: 5, label: 'Broker Partner' },
  { value: 6, label: 'Account Manager (CSR)' },
  { value: 7, label: 'Account Assistant (TSR)' },
];

// ── User picker — matches UserListView card style ───────────────────────────

interface UserPickerProps {
  branchId: number;
  onGrant: (member: APIBaraBranchMember) => Promise<void>;
  grantingId: number | null;
}

const UserPicker = ({ branchId, onGrant, grantingId }: UserPickerProps) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'navy.900');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  const [showSearch, setShowSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [members, setMembers] = useState<APIBaraBranchMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchMembers = useCallback(
    (searchVal: string, roleVal: string) => {
      setLoading(true);
      apiGetBranchMembers(branchId, {
        search: searchVal || undefined,
        role: roleVal ? parseInt(roleVal, 10) : undefined,
      })
        .then((data: any) => {
          setMembers(Array.isArray(data) ? data : []);
          setFetched(true);
        })
        .catch(() => setMembers([]))
        .finally(() => setLoading(false));
    },
    [branchId],
  );

  // Fetch members when card first becomes visible
  useEffect(() => {
    if (!fetched) fetchMembers('', '');
  }, [fetched, fetchMembers]);

  const handleSearchChange = (val: string) => {
    setSearch(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(
      () => fetchMembers(val, roleFilter),
      300,
    );
  };

  const handleRoleChange = (val: string) => {
    setRoleFilter(val);
    fetchMembers(search, val);
  };

  const handleReset = () => {
    setSearch('');
    setRoleFilter('');
    fetchMembers('', '');
  };

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      {/* Header */}
      <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
        <Text
          color={textColor}
          fontSize="lg"
          fontWeight="700"
          lineHeight="100%"
        >
          Add Delegate
        </Text>
        <Flex gap={2}>
          <Tooltip label={showSearch ? 'Hide search' : 'Show search'} hasArrow>
            <IconButton
              aria-label={showSearch ? 'Hide search' : 'Show search'}
              icon={<SearchIcon />}
              variant={showSearch ? 'solid' : 'ghost'}
              colorScheme={showSearch ? 'yellow' : 'gray'}
              onClick={() => setShowSearch(!showSearch)}
              size="md"
            />
          </Tooltip>
          <Tooltip label="Refresh" hasArrow>
            <IconButton
              aria-label="Refresh"
              icon={<RepeatIcon />}
              variant="ghost"
              isLoading={loading}
              onClick={() => fetchMembers(search, roleFilter)}
            />
          </Tooltip>
        </Flex>
      </Flex>

      {/* Search */}
      <Collapse in={showSearch} animateOpacity>
        <Box
          px="20px"
          pb="20px"
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <Stack
            spacing={4}
            direction={{ base: 'column', lg: 'row' }}
            align="flex-end"
          >
            <Box flex="1">
              <InputGroup>
                <Input
                  variant="main"
                  placeholder="Search by name or email"
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  bg={inputBg}
                  size="md"
                />
              </InputGroup>
            </Box>
            <Box flex="1">
              <Select
                variant="main"
                placeholder="All roles"
                value={roleFilter}
                onChange={(e) => handleRoleChange(e.target.value)}
                bg={inputBg}
                size="md"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </Select>
            </Box>
            <Flex gap={2}>
              <Button
                onClick={handleReset}
                variant="outline"
                leftIcon={<SmallCloseIcon />}
                size="md"
              >
                Reset
              </Button>
            </Flex>
          </Stack>
        </Box>
      </Collapse>

      {/* Table */}
      <Table variant="simple" color="gray.500">
        <Thead>
          <Tr>
            <Th pe="10px" borderColor={borderColor} color="gray.400">
              Name
            </Th>
            <Th pe="10px" borderColor={borderColor} color="gray.400">
              Email
            </Th>
            <Th pe="10px" borderColor={borderColor} color="gray.400">
              Role
            </Th>
            <Th borderColor={borderColor} />
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr>
              <Td colSpan={4} textAlign="center">
                <Flex align="center" justify="center" mt="20px" mb="20px">
                  <Spinner size="sm" mr="10px" /> Loading users...
                </Flex>
              </Td>
            </Tr>
          ) : members.length === 0 ? (
            <Tr>
              <Td colSpan={4} textAlign="center" color="gray.400">
                {fetched ? 'No eligible users found.' : ''}
              </Td>
            </Tr>
          ) : (
            members.map((m) => (
              <Tr key={m.id} cursor="pointer" _hover={{ bg: hoverBg }}>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {m.name}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    {m.email}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    {m.role_display}
                  </Text>
                </Td>
                <Td borderColor={borderColor} isNumeric>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    isLoading={grantingId === m.id}
                    onClick={() => onGrant(m)}
                  >
                    Grant Delegate
                  </Button>
                </Td>
              </Tr>
            ))
          )}
        </Tbody>
      </Table>
    </Card>
  );
};

// ── Main branch settings component ─────────────────────────────────────────

const BranchSettingsContent = ({ branchId }: { branchId: number }) => {
  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'navy.900');

  // ── Holidays ───────────────────────────────────────────────────────────────
  const [holidays, setHolidays] = useState<APIBaraHoliday[]>([]);
  const [loadingHolidays, setLoadingHolidays] = useState(true);
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayName, setNewHolidayName] = useState('');
  const [addingHoliday, setAddingHoliday] = useState(false);
  const [deletingHolidayId, setDeletingHolidayId] = useState<number | null>(
    null,
  );

  const fetchHolidays = useCallback(async () => {
    setLoadingHolidays(true);
    try {
      const data: any = await apiListBranchHolidays(branchId);
      setHolidays(Array.isArray(data) ? data : []);
    } catch {
      toastRef.current({
        title: 'Failed to load holidays',
        status: 'error',
        duration: 3000,
        position: 'top',
      });
    } finally {
      setLoadingHolidays(false);
    }
  }, [branchId]);

  const handleAddHoliday = async () => {
    if (!newHolidayDate || !newHolidayName.trim()) return;
    setAddingHoliday(true);
    try {
      await apiAddBranchHoliday(branchId, {
        date: newHolidayDate,
        name: newHolidayName.trim(),
      });
      setNewHolidayDate('');
      setNewHolidayName('');
      toast({
        title: 'Holiday added',
        status: 'success',
        duration: 3000,
        position: 'top',
      });
      const data: any = await apiListBranchHolidays(branchId);
      setHolidays(Array.isArray(data) ? data : []);
    } catch {
      toast({
        title: 'Failed to add holiday',
        status: 'error',
        duration: 3000,
        position: 'top',
      });
    } finally {
      setAddingHoliday(false);
    }
  };

  const handleDeleteHoliday = async (holidayId: number) => {
    setDeletingHolidayId(holidayId);
    try {
      await apiDeleteBranchHoliday(branchId, holidayId);
      setHolidays((prev) => prev.filter((h) => h.id !== holidayId));
      toast({
        title: 'Holiday removed',
        status: 'success',
        duration: 3000,
        position: 'top',
      });
    } catch {
      toast({
        title: 'Failed to remove holiday',
        status: 'error',
        duration: 3000,
        position: 'top',
      });
    } finally {
      setDeletingHolidayId(null);
    }
  };

  // ── Authority ──────────────────────────────────────────────────────────────
  const [authorities, setAuthorities] = useState<APIBaraAuthority[]>([]);
  const [loadingAuthorities, setLoadingAuthorities] = useState(true);
  const [grantingId, setGrantingId] = useState<number | null>(null);
  const [revokingAuthorityId, setRevokingAuthorityId] = useState<number | null>(
    null,
  );
  const [unrevokingAuthorityId, setUnrevokingAuthorityId] = useState<
    number | null
  >(null);
  // Increment to force UserPicker to re-fetch its member list after a grant
  const [memberRefreshKey, setMemberRefreshKey] = useState(0);

  const fetchAuthorities = useCallback(async () => {
    setLoadingAuthorities(true);
    try {
      const data: any = await apiListBranchAuthority(branchId);
      setAuthorities(Array.isArray(data) ? data : []);
    } catch {
      toastRef.current({
        title: 'Failed to load authorities',
        status: 'error',
        duration: 3000,
        position: 'top',
      });
    } finally {
      setLoadingAuthorities(false);
    }
  }, [branchId]);

  const handleGrant = async (member: APIBaraBranchMember) => {
    setGrantingId(member.id);
    try {
      await apiGrantBranchAuthority(branchId, {
        user_id: member.id,
      });
      toast({
        title: 'Delegate authority granted',
        status: 'success',
        duration: 3000,
        position: 'top',
      });
      setMemberRefreshKey((k) => k + 1);
      const data: any = await apiListBranchAuthority(branchId);
      setAuthorities(Array.isArray(data) ? data : []);
    } catch {
      toast({
        title: 'Failed to grant authority',
        status: 'error',
        duration: 3000,
        position: 'top',
      });
    } finally {
      setGrantingId(null);
    }
  };

  const handleRevokeAuthority = async (authorityId: number) => {
    setRevokingAuthorityId(authorityId);
    try {
      await apiRevokeBranchAuthority(branchId, authorityId);
      setAuthorities((prev) =>
        prev.map((a) =>
          a.id === authorityId ? { ...a, is_active: false } : a,
        ),
      );
      toast({
        title: 'Authority revoked',
        status: 'success',
        duration: 3000,
        position: 'top',
      });
    } catch {
      toast({
        title: 'Failed to revoke authority',
        status: 'error',
        duration: 3000,
        position: 'top',
      });
    } finally {
      setRevokingAuthorityId(null);
    }
  };

  const handleUnrevokeAuthority = async (authorityId: number) => {
    setUnrevokingAuthorityId(authorityId);
    const authority = authorities.find((a) => a.id === authorityId);
    if (!authority) return;
    try {
      await apiGrantBranchAuthority(branchId, {
        user_id: authority.user_id,
      });
      setAuthorities((prev) =>
        prev.map((a) => (a.id === authorityId ? { ...a, is_active: true } : a)),
      );
      toast({
        title: 'Authority reinstated',
        status: 'success',
        duration: 3000,
        position: 'top',
      });
    } catch {
      toast({
        title: 'Failed to reinstate authority',
        status: 'error',
        duration: 3000,
        position: 'top',
      });
    } finally {
      setUnrevokingAuthorityId(null);
    }
  };

  useEffect(() => {
    fetchHolidays();
    fetchAuthorities();
  }, [fetchHolidays, fetchAuthorities]);

  return (
    <VStack align="stretch" spacing={6}>
      {/* ── Holiday Calendar ── */}
      <Card
        flexDirection="column"
        w="100%"
        px="0px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
      >
        <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
          <Text
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
          >
            Holiday Calendar
          </Text>
        </Flex>

        <Box
          px="20px"
          pb="20px"
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <Stack
            spacing={4}
            direction={{ base: 'column', lg: 'row' }}
            align="flex-end"
          >
            <Box>
              <FormLabel fontSize="sm" mb={1}>
                Date
              </FormLabel>
              <Input
                type="date"
                variant="main"
                size="md"
                value={newHolidayDate}
                onChange={(e) => setNewHolidayDate(e.target.value)}
                bg={inputBg}
                w="180px"
              />
            </Box>
            <Box flex="1">
              <FormLabel fontSize="sm" mb={1}>
                Name
              </FormLabel>
              <Input
                variant="main"
                size="md"
                placeholder="e.g. Civic Holiday"
                value={newHolidayName}
                onChange={(e) => setNewHolidayName(e.target.value)}
                bg={inputBg}
              />
            </Box>
            <Button
              colorScheme="blue"
              size="md"
              onClick={handleAddHoliday}
              isLoading={addingHoliday}
              isDisabled={!newHolidayDate || !newHolidayName.trim()}
            >
              Add Holiday
            </Button>
          </Stack>
        </Box>

        <Table variant="simple" color="gray.500">
          <Thead>
            <Tr>
              <Th pe="10px" borderColor={borderColor} color="gray.400">
                Date
              </Th>
              <Th pe="10px" borderColor={borderColor} color="gray.400">
                Name
              </Th>
              <Th borderColor={borderColor} />
            </Tr>
          </Thead>
          <Tbody>
            {loadingHolidays ? (
              <Tr>
                <Td colSpan={3} textAlign="center">
                  <Flex align="center" justify="center" mt="20px" mb="20px">
                    <Spinner size="sm" mr="10px" /> Loading...
                  </Flex>
                </Td>
              </Tr>
            ) : holidays.length === 0 ? (
              <Tr>
                <Td colSpan={3} textAlign="center" color="gray.400">
                  No holidays configured.
                </Td>
              </Tr>
            ) : (
              holidays.map((h) => (
                <Tr key={h.id}>
                  <Td borderColor={borderColor}>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {h.date}
                    </Text>
                  </Td>
                  <Td borderColor={borderColor}>
                    <Text color={textColor} fontSize="sm">
                      {h.name}
                    </Text>
                  </Td>
                  <Td borderColor={borderColor} isNumeric>
                    <Button
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      isLoading={deletingHolidayId === h.id}
                      onClick={() => handleDeleteHoliday(h.id)}
                    >
                      Remove
                    </Button>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Card>

      <Divider />

      {/* ── Add Delegate (user picker) ── */}
      <UserPicker
        key={memberRefreshKey}
        branchId={branchId}
        onGrant={handleGrant}
        grantingId={grantingId}
      />

      {/* ── Current Authorities ── */}
      <Card
        flexDirection="column"
        w="100%"
        px="0px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
      >
        <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
          <Text
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
          >
            Current Authorities
          </Text>
        </Flex>

        <Table variant="simple" color="gray.500">
          <Thead>
            <Tr>
              <Th pe="10px" borderColor={borderColor} color="gray.400">
                User
              </Th>
              <Th pe="10px" borderColor={borderColor} color="gray.400">
                Email
              </Th>
              <Th pe="10px" borderColor={borderColor} color="gray.400">
                Type
              </Th>
              <Th pe="10px" borderColor={borderColor} color="gray.400">
                Status
              </Th>
              <Th borderColor={borderColor} />
            </Tr>
          </Thead>
          <Tbody>
            {loadingAuthorities ? (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  <Flex align="center" justify="center" mt="20px" mb="20px">
                    <Spinner size="sm" mr="10px" /> Loading...
                  </Flex>
                </Td>
              </Tr>
            ) : authorities.length === 0 ? (
              <Tr>
                <Td colSpan={5} textAlign="center" color="gray.400">
                  No authorities configured.
                </Td>
              </Tr>
            ) : (
              authorities.map((a) => (
                <Tr key={a.id}>
                  <Td borderColor={borderColor}>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {a.user_name}
                    </Text>
                  </Td>
                  <Td borderColor={borderColor}>
                    <Text color={textColor} fontSize="sm">
                      {a.user_email}
                    </Text>
                  </Td>
                  <Td borderColor={borderColor}>
                    <Badge
                      colorScheme={
                        AUTHORITY_TYPE_COLORS[a.authority_type] ?? 'gray'
                      }
                    >
                      {a.authority_type_display}
                    </Badge>
                  </Td>
                  <Td borderColor={borderColor}>
                    <Badge colorScheme={a.is_active ? 'green' : 'red'}>
                      {a.is_active ? 'Active' : 'Suspended'}
                    </Badge>
                  </Td>
                  <Td borderColor={borderColor} isNumeric>
                    {a.is_active ? (
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        isLoading={revokingAuthorityId === a.id}
                        onClick={() => handleRevokeAuthority(a.id)}
                      >
                        Revoke
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        colorScheme="green"
                        variant="ghost"
                        isLoading={unrevokingAuthorityId === a.id}
                        onClick={() => handleUnrevokeAuthority(a.id)}
                      >
                        Reinstate
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Card>
    </VStack>
  );
};

const BranchSettingsView = () => {
  const { branchId } = useParams();

  return (
    <BaraFeatureGuard>
      {branchId ? (
        <BranchSettingsContent branchId={parseInt(branchId, 10)} />
      ) : (
        <Text color="red.500">No branch ID provided.</Text>
      )}
    </BaraFeatureGuard>
  );
};

export default BranchSettingsView;
